import { Textarea } from '@/components/ui/textarea';
import type { ChangeEventHandler, RefObject } from 'react';
import { useI18n } from '@/providers/i18n';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePreviewCanvas } from '@/hooks/use-preview-canvas';
import { usePromptSummon } from '@/components/nodes/shared/use-prompt-summon';
import type { IncomingImageInput } from '@/components/nodes/shared/use-incoming-image-inputs';
import { hasMention, renderMentionMirror, detectMentionAnchor, resolveMentionCaret, handleMentionAtomicKeys } from '@/components/nodes/shared/mention-mirror';
import { Maximize2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useReactFlow } from '@xyflow/react';
import { buildMentionNameSuffix } from '@/components/nodes/shared/mention-mirror';

type InstructionInputProps = {
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  value: string;
  placeholder?: string;
  onChange: ChangeEventHandler<HTMLTextAreaElement>;
  onInput?: React.FormEventHandler<HTMLTextAreaElement>;
  onFocus?: () => void;
  onBlur?: () => void;
  /**
   * 显式提交回调：在「展开编辑」对话框点击 Save 时触发，
   * 用于让父级把最新值立刻持久化到节点数据
   * （避免对话框保存后因 textarea 未失焦而错过 onBlur，
   *  导致随后点击 Generate 仍使用旧 prompt 的 bug）。
   */
  onCommit?: (value: string) => void;
  showPlaceholder: boolean;
  maxHeightPx?: number;
  imageInputs?: IncomingImageInput[];
  hideExpandButton?: boolean;
};

export type InstructionInputHandle = {
  openEditor: () => void;
};

export const InstructionInput = forwardRef<InstructionInputHandle, InstructionInputProps>(function InstructionInput({
  textareaRef,
  value,
  placeholder,
  onChange,
  onInput,
  onFocus,
  onBlur,
  onCommit,
  showPlaceholder,
  maxHeightPx = 130,
  imageInputs = [],
  hideExpandButton = false,
}, ref) {
  const { t } = useI18n();
  const isPreview = usePreviewCanvas();
  const { getNode } = useReactFlow();

  // IME（中文输入法等）期间使用本地状态缓存，避免受控重渲染导致光标跳动
  const [isComposing, setIsComposing] = useState(false);
  const [innerValue, setInnerValue] = useState<string>(value ?? '');

  // @ mention
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionListIdx, setMentionListIdx] = useState(0);
  const mentionAnchorRef = useRef<number | null>(null);
  const mirrorRef = useRef<HTMLDivElement>(null);
  const [mentionPos, setMentionPos] = useState<{ left: number; top: number; width: number } | null>(null);

  // Expanded editor dialog
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorValue, setEditorValue] = useState('');
  const dialogTextareaRef = useRef<HTMLTextAreaElement>(null);
  const dialogMirrorRef = useRef<HTMLDivElement>(null);
  const [dlgMentionOpen, setDlgMentionOpen] = useState(false);
  const [dlgMentionListIdx, setDlgMentionListIdx] = useState(0);
  const dlgMentionAnchorRef = useRef<number | null>(null);
  const [dlgMentionPos, setDlgMentionPos] = useState<{ left: number; top: number; width: number } | null>(null);

  const openEditorDialog = useCallback(() => {
    setEditorValue(value ?? '');
    setDlgMentionOpen(false);
    setEditorOpen(true);
  }, [value]);

  useImperativeHandle(ref, () => ({ openEditor: openEditorDialog }), [openEditorDialog]);

  // 父 value 变化时，同步到本地；合成期间不覆盖本地缓存
  useEffect(() => {
    if (!isComposing) {
      setInnerValue(value ?? '');
    }
  }, [value, isComposing]);

  /**
   * 把 textarea 的滚动位置同步到高亮镜像层。
   * 仅靠 onScroll 不够：镜像层在「第一次出现 @ 引用」时才挂载（scrollTop=0），
   * 若此时 textarea 已经滚动过，两层会整体错开几行，表现为「光标和文字错位」。
   */
  const syncMirrorScroll = () => {
    const ta = textareaRef.current;
    const mi = mirrorRef.current;
    if (ta && mi && mi.scrollTop !== ta.scrollTop) mi.scrollTop = ta.scrollTop;
    const dta = dialogTextareaRef.current;
    const dmi = dialogMirrorRef.current;
    if (dta && dmi && dmi.scrollTop !== dta.scrollTop) dmi.scrollTop = dta.scrollTop;
  };
  // 每次渲染（内容变化 / 镜像层挂载）后都对齐一次；只读写 scrollTop，开销可忽略
  useLayoutEffect(() => {
    syncMirrorScroll();
  });

  const adjustHeight = () => {
    const el = textareaRef.current;
    if (!el) return;
    try {
      el.style.height = 'auto';
      const nextHeight = Math.max(36, Math.min(el.scrollHeight, maxHeightPx));
      el.style.height = `${nextHeight}px`;
      // 只有真的溢出时才显示滚动条（避免常驻滚动条影响观感）
      el.style.overflowY = el.scrollHeight > maxHeightPx ? 'auto' : 'hidden';
      // height 先置 auto 会让 textarea 的 scrollTop 被钳制/重置，镜像层需跟着对齐
      syncMirrorScroll();
    } catch {}
  };

  const actualValue = isComposing ? innerValue : value;
  useEffect(() => {
    adjustHeight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualValue, maxHeightPx]);

  const promptSummon = usePromptSummon({
    textareaRef,
    enabled: !isPreview,
    isComposing,
    t,
    onApplyValue: (nextValue, caretPos) => {
      setInnerValue(nextValue);
      try { onChange({ target: { value: nextValue } } as any); } catch {}
      requestAnimationFrame(() => {
        try {
          const el = textareaRef.current;
          if (!el) return;
          el.focus();
          el.setSelectionRange(caretPos, caretPos);
          adjustHeight();
        } catch {}
      });
    },
  });

  const handleInput: React.FormEventHandler<HTMLTextAreaElement> = (e) => {
    adjustHeight();
    promptSummon.syncFromTextarea(e.currentTarget);
    try { onInput?.(e); } catch {}
  };
  // 合成事件处理：在合成期间仅更新本地值，不向上传递 onChange，结束后一次性提交
  const handleCompositionStart: React.CompositionEventHandler<HTMLTextAreaElement> = () => {
    setIsComposing(true);
    promptSummon.close();
  };
  const handleCompositionUpdate: React.CompositionEventHandler<HTMLTextAreaElement> = (e) => {
    setInnerValue(e.currentTarget.value);
  };
  const handleCompositionEnd: React.CompositionEventHandler<HTMLTextAreaElement> = (e) => {
    setIsComposing(false);
    const finalValue = e.currentTarget.value;
    setInnerValue(finalValue);
    // 构造一次变更事件上抛，保持父级受控数据一致
    try {
      onChange({ target: { value: finalValue } } as any);
    } catch {}
  };
  const handleChange: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    const next = e.target.value;
    setInnerValue(next);
    if (!isComposing) {
      try { onChange(e); } catch {}
    }

    // detect @ mention
    if (imageInputs.length > 0) {
      const el = e.target;
      const atIdx = detectMentionAnchor(next, el.selectionStart ?? next.length);
      if (atIdx !== null) {
        mentionAnchorRef.current = atIdx;
        setMentionOpen(true);
        setMentionListIdx(0);
        try {
          const rect = el.getBoundingClientRect();
          setMentionPos({ left: rect.left, top: rect.bottom + 4, width: rect.width });
        } catch {}
      } else {
        setMentionOpen(false);
      }
    }
  };

  const buildMentionLabel = useCallback(
    (img: IncomingImageInput, prefix: string): string => {
      // 主体聚合项：插入格式  `主体名（@[图像1] @[图像2] @[图像3]） `
      if (img.kind === 'subjectGroup' && Array.isArray(img.groupChildIdxs) && img.groupChildIdxs.length > 0) {
        const parts: string[] = [];
        for (const ci of img.groupChildIdxs) {
          const child = imageInputs[ci];
          if (!child) continue;
          const tl = child.mediaType === 'video' ? t('Video') : child.mediaType === 'audio' ? t('Audio') : t('Image');
          parts.push(`@[${tl}${child.idx}]`);
        }
        if (parts.length === 0) return '';
        const subjectName = String(img.label || '').trim() || t('Image Group');
        return `${prefix}${subjectName}（${parts.join(' ')}） `;
      }
      const tl = img.mediaType === 'video' ? t('Video') : img.mediaType === 'audio' ? t('Audio') : t('Image');
      // 上游节点被用户命名（节点标题 / 素材备注）时，把名字一并写进 token：@[图像1·海螺]
      // 仅用于展示，发送给模型前由 normalizeMentions 去掉名字部分。
      const suffix = buildMentionNameSuffix(img, img.sourceId ? getNode(img.sourceId) : undefined, t);
      return `${prefix}@[${tl}${img.idx}${suffix}] `;
    },
    [imageInputs, t, getNode],
  );

  const insertMention = useCallback((imgIdx: number) => {
    const anchor = mentionAnchorRef.current;
    if (anchor === null) return;
    const el = textareaRef.current;
    const current = el?.value ?? value;
    const cursorPos = el?.selectionStart ?? current.length;
    const img = imageInputs[imgIdx];
    if (!img) return;
    const prefix = anchor > 0 && !/\s/.test(current[anchor - 1] ?? '') ? ' ' : '';
    const label = buildMentionLabel(img, prefix);
    if (!label) return;
    const next = current.slice(0, anchor) + label + current.slice(cursorPos);
    setInnerValue(next);
    try { onChange({ target: { value: next } } as any); } catch {}
    setMentionOpen(false);
    mentionAnchorRef.current = null;
    setTimeout(() => {
      if (el) {
        const pos = anchor + label.length;
        el.selectionStart = pos;
        el.selectionEnd = pos;
        el.focus();
      }
    }, 0);
  }, [value, imageInputs, buildMentionLabel, onChange, textareaRef]);

  const handleMentionKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>): boolean => {
    // mention menu navigation
    if (mentionOpen && imageInputs.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionListIdx(i => (i + 1) % imageInputs.length); return true; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setMentionListIdx(i => (i - 1 + imageInputs.length) % imageInputs.length); return true; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(mentionListIdx); return true; }
      if (e.key === 'Escape') { e.preventDefault(); setMentionOpen(false); return true; }
    }

    return handleMentionAtomicKeys(e, (next) => {
      setInnerValue(next);
      try { onChange({ target: { value: next } } as any); } catch {}
    });
  }, [mentionOpen, imageInputs.length, mentionListIdx, insertMention, onChange]);

  const shouldShowPlaceholder = showPlaceholder && !actualValue?.trim();

  const showMirror = hasMention(actualValue);

  const handleEditorSave = () => {
    setInnerValue(editorValue);
    try { onChange({ target: { value: editorValue } } as any); } catch {}
    // 关键：对话框保存后必须立刻把值写入节点数据。
    // 否则 textarea 未发生 blur，父级仅更新了本地缓存（localInstructions），
    // 用户随即点击 Generate 时 handleGenerate 仍会读到旧的 data.instructions。
    try { onCommit?.(editorValue); } catch {}
    setDlgMentionOpen(false);
    setEditorOpen(false);
  };

  const renderMentionItems = (
    activeIdx: number,
    onSelect: (i: number) => void,
    onHover: (i: number) => void,
  ) =>
    imageInputs.map((img, i) => {
      const isGroup = img.kind === 'subjectGroup';
      const childCount = isGroup ? (img.groupChildIdxs?.length ?? img.subTotal ?? 0) : 0;
      return (
      <button
        key={img.rid || img.sourceId}
        type="button"
        className={`flex items-center gap-2 w-full rounded-md px-2 py-2 text-xs text-left transition-colors ${i === activeIdx ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/60'}`}
        onMouseDown={(e) => { e.preventDefault(); onSelect(i); }}
        onMouseEnter={() => onHover(i)}
      >
        {isGroup ? (
          <span className="w-6 h-6 rounded-md bg-primary/10 ring-1 ring-primary/25 shrink-0 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary"><path d="M18 22H4a2 2 0 0 1-2-2V6"/><path d="m22 13-1.296-1.296a2.41 2.41 0 0 0-3.408 0L11 18"/><circle cx="12" cy="8" r="2"/><rect width="16" height="16" x="6" y="2" rx="2"/></svg>
          </span>
        ) : img.mediaType === 'video' ? (
          <span className="w-6 h-6 rounded-md bg-muted/60 shrink-0 border border-border/30 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground"><path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.934a.5.5 0 0 0-.777-.416L16 11"/><rect x="2" y="6" width="14" height="12" rx="2"/></svg>
          </span>
        ) : img.mediaType === 'audio' ? (
          <span className="w-6 h-6 rounded-md bg-muted/60 shrink-0 border border-border/30 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
          </span>
        ) : (
          <span className="w-6 h-6 rounded-md bg-muted/60 shrink-0 border border-border/30 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
          </span>
        )}
        {isGroup ? (
          <span className="truncate text-sm font-medium">
            {img.label || t('Image Group')}
            {childCount > 0 ? <span className="ml-1 text-muted-foreground">×{childCount}</span> : null}
          </span>
        ) : (
          <span className="truncate text-sm font-medium">{img.mediaType === 'video' ? t('Video') : img.mediaType === 'audio' ? t('Audio') : t('Image')}{img.idx}{img.label ? `（${img.label}）` : ''}</span>
        )}
      </button>
    );
    });

  const getCaretPixelPos = (el: HTMLTextAreaElement, pos: number): { top: number; left: number } => {
    const div = document.createElement('div');
    const computed = window.getComputedStyle(el);
    const stylesToCopy = [
      'font-family', 'font-size', 'font-weight', 'font-style', 'line-height',
      'letter-spacing', 'word-spacing', 'text-indent', 'text-transform', 'tab-size',
      'box-sizing', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
      'border-top-width', 'border-right-width', 'border-bottom-width', 'border-left-width',
      'width',
    ];
    for (const p of stylesToCopy) div.style.setProperty(p, computed.getPropertyValue(p));
    div.style.position = 'absolute';
    div.style.visibility = 'hidden';
    div.style.whiteSpace = 'pre-wrap';
    div.style.wordWrap = 'break-word';
    div.style.overflow = 'hidden';
    div.style.height = 'auto';
    div.textContent = el.value.substring(0, pos);
    const span = document.createElement('span');
    span.textContent = '\u200b';
    div.appendChild(span);
    document.body.appendChild(div);
    const top = span.offsetTop - el.scrollTop;
    const left = span.offsetLeft - el.scrollLeft;
    document.body.removeChild(div);
    return { top, left };
  };

  const handleDialogChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value;
    setEditorValue(next);
    if (imageInputs.length > 0) {
      const el = e.target;
      const cursorPos = el.selectionStart ?? next.length;
      const atIdx = detectMentionAnchor(next, cursorPos);
      if (atIdx !== null) {
        dlgMentionAnchorRef.current = atIdx;
        setDlgMentionOpen(true);
        setDlgMentionListIdx(0);
        try {
          const rect = el.getBoundingClientRect();
          const caret = getCaretPixelPos(el, cursorPos);
          const lineHeight = parseInt(window.getComputedStyle(el).lineHeight) || 20;
          setDlgMentionPos({
            left: rect.left + caret.left,
            top: rect.top + caret.top + lineHeight + 4,
            width: 280,
          });
        } catch {}
      } else {
        setDlgMentionOpen(false);
      }
    }
  };

  const insertDialogMention = (imgIdx: number) => {
    const anchor = dlgMentionAnchorRef.current;
    if (anchor === null) return;
    const el = dialogTextareaRef.current;
    const current = el?.value ?? editorValue;
    const cursorPos = el?.selectionStart ?? current.length;
    const img = imageInputs[imgIdx];
    if (!img) return;
    const prefix = anchor > 0 && !/\s/.test(current[anchor - 1] ?? '') ? ' ' : '';
    const label = buildMentionLabel(img, prefix);
    if (!label) return;
    const next = current.slice(0, anchor) + label + current.slice(cursorPos);
    setEditorValue(next);
    setDlgMentionOpen(false);
    dlgMentionAnchorRef.current = null;
    setTimeout(() => {
      if (el) {
        const pos = anchor + label.length;
        el.selectionStart = pos;
        el.selectionEnd = pos;
        el.focus();
      }
    }, 0);
  };

  const handleDialogKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleEditorSave();
      return;
    }
    if (dlgMentionOpen && imageInputs.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setDlgMentionListIdx(i => (i + 1) % imageInputs.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setDlgMentionListIdx(i => (i - 1 + imageInputs.length) % imageInputs.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertDialogMention(dlgMentionListIdx); return; }
      if (e.key === 'Escape') { e.preventDefault(); setDlgMentionOpen(false); return; }
    }
    handleMentionAtomicKeys(e, setEditorValue);
  };

  return (
    <>
      {shouldShowPlaceholder && (
        <div
          className="pointer-events-none absolute inset-0 text-sm placeholder-flow flex items-start px-2.5 py-2 leading-5"
        >
          {placeholder ?? t('Please provide drawing instructions here...')}
        </div>
      )}
      {showMirror && (
        <div
          ref={mirrorRef}
          aria-hidden
          className="pointer-events-none absolute inset-0 text-sm leading-5 px-2.5 py-2 whitespace-pre-wrap break-words overflow-y-auto overflow-x-hidden z-[1] [scrollbar-gutter:stable] font-[inherit] [text-rendering:geometricPrecision] custom-scrollbar"
        >
          {renderMentionMirror(actualValue)}
        </div>
      )}
      <Textarea
        ref={textareaRef}
        value={actualValue}
        onChange={handleChange}
        readOnly={isPreview}
        placeholder=""
        onContextMenuCapture={(e) => {
          // Canvas is wrapped in a Radix ContextMenuTrigger, which will swallow the native
          // browser context menu (copy/paste) inside inputs. Stop propagation so the
          // textarea can use the native context menu.
          // IMPORTANT: Do NOT preventDefault here.
          try {
            e.stopPropagation();
            // Extra guard: prevent Radix / other listeners from seeing this event.
            // (Some libs attach native listeners outside React's bubble phase.)
            (e.nativeEvent as any)?.stopImmediatePropagation?.();
          } catch {}
        }}
        onFocus={(e) => { promptSummon.syncFromTextarea(e.currentTarget); try { onFocus?.(); } catch {} }}
        onBlur={(e) => {
          if ((e.relatedTarget as HTMLElement)?.closest?.('[data-mention-list]')) return;
          promptSummon.close();
          setMentionOpen(false);
          try { textareaRef.current && (textareaRef.current.scrollTop = 0); } catch {}
          try { onBlur?.(); } catch {}
        }}
        onClick={(e) => { resolveMentionCaret(e.currentTarget, true); promptSummon.syncFromTextarea(e.currentTarget); }}
        onKeyUp={(e) => { resolveMentionCaret(e.currentTarget, false); promptSummon.syncFromTextarea(e.currentTarget); }}
        onKeyDown={(e) => {
          if (handleMentionKeyDown(e)) return;
          promptSummon.onKeyDown(e);
        }}
        // ReactFlow: prevent dragging the whole node when selecting text inside textarea
        className={`nodrag nowheel min-h-[36px] w-full resize-none border-none bg-transparent dark:bg-transparent shadow-none focus-visible:ring-0 text-sm leading-5 px-2.5 py-2 whitespace-pre-wrap break-words overflow-y-auto custom-scrollbar relative z-[2] font-[inherit] [text-rendering:geometricPrecision] [scrollbar-gutter:stable] ${showMirror ? 'text-transparent caret-foreground' : ''}`}
        rows={1}
        style={{
          height: 'auto',
          minHeight: '36px',
          maxHeight: `${maxHeightPx}px`,
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
        }}
        onInput={handleInput}
        onScroll={(e) => { if (mirrorRef.current) mirrorRef.current.scrollTop = e.currentTarget.scrollTop; }}
        onCompositionStart={handleCompositionStart}
        onCompositionUpdate={handleCompositionUpdate}
        onCompositionEnd={handleCompositionEnd}
      />

      {promptSummon.portal}

      {mentionOpen && imageInputs.length > 0 && mentionPos && typeof document !== 'undefined' && createPortal(
        <div
          data-mention-list
          className="nodrag nowheel fixed z-[9999] rounded-xl border bg-popover shadow-2xl backdrop-blur-sm overflow-hidden"
          style={{ left: `${mentionPos.left}px`, top: `${mentionPos.top}px`, width: `${mentionPos.width}px` }}
        >
          <div className="p-1 max-h-[200px] overflow-y-auto custom-scrollbar">
            {renderMentionItems(mentionListIdx, insertMention, setMentionListIdx)}
          </div>
        </div>,
        document.body
      )}

      {!isPreview && !hideExpandButton && (
        <button
          type="button"
          className="nodrag absolute right-1 top-[7px] z-[3] rounded-md p-1 text-muted-foreground/40 hover:text-muted-foreground hover:bg-sidebar-accent/60 transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            openEditorDialog();
          }}
          title={t('Open Editor')}
        >
          <Maximize2 size={14} />
        </button>
      )}

      <Dialog open={editorOpen} onOpenChange={(open) => { setEditorOpen(open); if (!open) setDlgMentionOpen(false); }}>
        <DialogContent
          className="sm:max-w-3xl max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden rounded-3xl border-none bg-sidebar/95 backdrop-blur-2xl shadow-2xl"
          // Prevent closing by clicking outside so unsaved prompt edits aren't lost
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogTitle className="text-base font-bold tracking-tight text-foreground/90">
              {t('Edit Prompt')}
            </DialogTitle>
          </DialogHeader>
          <div className="px-6 pb-6">
            <div className="rounded-2xl border border-sidebar-border/30 bg-sidebar-accent/30 overflow-hidden h-[400px] flex flex-col">
              <div className="relative flex-1 flex flex-col min-h-0">
                {hasMention(editorValue) && (
                  <div
                    ref={dialogMirrorRef}
                    aria-hidden
                    className="pointer-events-none absolute inset-0 text-sm leading-relaxed p-6 whitespace-pre-wrap break-words overflow-y-auto z-[1] font-[inherit] custom-scrollbar"
                  >
                    {renderMentionMirror(editorValue)}
                  </div>
                )}
                <textarea
                  ref={dialogTextareaRef}
                  value={editorValue}
                  onChange={handleDialogChange}
                  placeholder={placeholder ?? t('Please provide instructions here...')}
                  className={`flex-1 w-full resize-none border-none bg-transparent p-6 text-sm leading-relaxed outline-none custom-scrollbar relative z-[2] ${hasMention(editorValue) ? 'text-transparent caret-foreground' : ''}`}
                  autoFocus
                  onContextMenuCapture={(e) => {
                    try {
                      e.stopPropagation();
                      (e.nativeEvent as any)?.stopImmediatePropagation?.();
                    } catch {}
                  }}
                  onClick={(e) => resolveMentionCaret(e.currentTarget, true)}
                  onKeyUp={(e) => resolveMentionCaret(e.currentTarget, false)}
                  onKeyDown={handleDialogKeyDown}
                  onBlur={(e) => {
                    if ((e.relatedTarget as HTMLElement)?.closest?.('[data-mention-list]')) return;
                    setDlgMentionOpen(false);
                  }}
                  onScroll={(e) => { if (dialogMirrorRef.current) dialogMirrorRef.current.scrollTop = e.currentTarget.scrollTop; }}
                />
              </div>
            </div>
          </div>
          <div className="px-6 py-4 border-t border-sidebar-border/30 shrink-0">
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="ghost"
                className="rounded-xl h-9 px-4 text-xs font-bold hover:bg-sidebar-accent"
                onClick={() => { setDlgMentionOpen(false); setEditorOpen(false); }}
              >
                {t('Cancel')}
              </Button>
              <Button
                size="sm"
                className="rounded-xl h-9 px-6 text-xs font-bold shadow-lg shadow-primary/20 transition-all active:scale-95"
                onClick={handleEditorSave}
              >
                {t('Save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {editorOpen && dlgMentionOpen && imageInputs.length > 0 && dlgMentionPos && typeof document !== 'undefined' && createPortal(
        <div
          data-mention-list
          className="fixed z-[9999] rounded-xl border bg-popover shadow-2xl backdrop-blur-sm overflow-hidden"
          style={{ left: `${dlgMentionPos.left}px`, top: `${dlgMentionPos.top}px`, width: `${dlgMentionPos.width}px` }}
        >
          <div className="p-1 max-h-[200px] overflow-y-auto custom-scrollbar">
            {renderMentionItems(dlgMentionListIdx, insertDialogMention, setDlgMentionListIdx)}
          </div>
        </div>,
        document.body
      )}
    </>
  );
});